import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  SCHEDULED_APPOINTMENTS_FILTERS,
  SCHEDULED_APPOINTMENTS_KPI_DEFINITIONS,
} from '@constants/Reports/scheduledAppointments.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import { useScheduledAppointmentsReport } from '@hooks/reports/useScheduledAppointmentsReport'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  computeDynamicCountAxis,
  computeDynamicDollarAxis,
  computePercentAxis,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CHART_MARGIN_LEGEND = {
  top: 24,
  right: 12,
  left: 4,
  bottom: 12,
}
const REPORT_LEGEND_WRAPPER_STYLE = {
  fontSize: 12,
  paddingTop: 0,
  lineHeight: '16px',
}
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_GROUPED_BAR_GAP = 4
const REPORT_YAXIS_WIDTH = 46

function formatProductionDollars(value: number): string {
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`
  }
  if (value >= 1_000) {
    return `$${Math.round(value / 1_000)}K`
  }
  return `$${value}`
}

function formatKpiNumber(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return Math.round(value).toLocaleString('en-US')
}

function formatKpiDecimal(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })
}

function ScheduledAppointmentsReport(): React.ReactElement {
  const [filledOpenView, setFilledOpenView] =
    useState<ReportsDistributionView>('Count')
  const [hygieneView, setHygieneView] =
    useState<ReportsDistributionView>('Count')
  const [productionView, setProductionView] =
    useState<ReportsDistributionView>('Count')
  const [noShowsView, setNoShowsView] =
    useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const { loading, error, data } = useScheduledAppointmentsReport(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage:
      'Failed to load scheduled appointments data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return SCHEDULED_APPOINTMENTS_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        value: loading ? '…' : '—',
      }))
    }

    const valueById: Record<string, number> = {
      'patients-seen': data.patientsSeen.value,
      'avg-filled-slots': data.avgFilledSlots.value,
      'avg-open-slots': data.avgOpenSlots.value,
      'avg-hygiene-gaps': data.avgHygieneGaps.value,
      'no-shows': data.noShows.value,
    }

    return SCHEDULED_APPOINTMENTS_KPI_DEFINITIONS.map((def) => ({
      id: def.id,
      title: def.title,
      footer: def.footer,
      value:
        def.format === 'number'
          ? formatKpiNumber(valueById[def.id])
          : formatKpiDecimal(valueById[def.id]),
    }))
  }, [data, loading])

  const filledOpenChartData = useMemo(() => {
    if (!data) return []
    const openByLabel = new Map(
      data.openSlotsByPeriod.buckets.map((b) => [b.label, b.value]),
    )
    const rows = data.filledSlotsByPeriod.buckets.map((row) => ({
      month: row.label,
      filled: row.value,
      open: openByLabel.get(row.label) ?? 0,
    }))
    return rows.map((row) => {
      const total = row.filled + row.open || 1
      if (filledOpenView === 'Count') {
        return {
          month: row.month,
          filledValue: row.filled,
          openValue: row.open,
          filledLabel: row.filled.toFixed(1),
          openLabel: row.open.toFixed(1),
        }
      }
      const filledPct = Math.round((row.filled / total) * 1000) / 10
      const openPct = Math.round((row.open / total) * 1000) / 10
      return {
        month: row.month,
        filledValue: filledPct,
        openValue: openPct,
        filledLabel: `${filledPct}%`,
        openLabel: `${openPct}%`,
      }
    })
  }, [data, filledOpenView])

  const filledOpenYAxis = useMemo(() => {
    if (filledOpenView === 'Percentage') {
      return {
        domain: [0, 100] as [number, number],
        ticks: [0, 25, 50, 75, 100],
        tickFormatter: (v: number) => `${v}%`,
      }
    }
    const values = filledOpenChartData.flatMap((row) => [
      row.filledValue,
      row.openValue,
    ])
    return computeDynamicCountAxis(values)
  }, [filledOpenView, filledOpenChartData])

  const hygieneChartData = useMemo(() => {
    if (!data) return []
    const avgFilled = data.avgFilledSlots.value || 1
    return data.hygieneGapsByPeriod.buckets.map((row) => {
      if (hygieneView === 'Count') {
        return {
          month: row.label,
          barValue: row.value,
          topLabel: row.value.toFixed(1),
        }
      }
      const pct = Math.round((row.value / avgFilled) * 1000) / 10
      return {
        month: row.label,
        barValue: pct,
        topLabel: `${pct}%`,
      }
    })
  }, [data, hygieneView])

  const hygieneYAxis = useMemo(() => {
    if (hygieneView === 'Percentage') {
      return computePercentAxis(hygieneChartData.map((row) => row.barValue))
    }
    return computeDynamicCountAxis(hygieneChartData.map((row) => row.barValue))
  }, [hygieneView, hygieneChartData])

  const productionChartData = useMemo(() => {
    if (!data) return []
    const targetByLabel = new Map(
      data.productionTargetByPeriod.buckets.map((b) => [b.label, b.value]),
    )
    const rows = data.scheduledProductionByPeriod.buckets.map((row) => ({
      month: row.label,
      scheduled: row.value,
      target: targetByLabel.get(row.label) ?? 0,
    }))
    return rows.map((row) => {
      if (productionView === 'Count') {
        return {
          month: row.month,
          scheduledValue: row.scheduled,
          targetValue: row.target,
          scheduledLabel: formatProductionDollars(row.scheduled),
          targetLabel: formatProductionDollars(row.target),
        }
      }
      const pct =
        row.target > 0
          ? Math.round((row.scheduled / row.target) * 1000) / 10
          : 0
      return {
        month: row.month,
        scheduledValue: pct,
        targetValue: 100,
        scheduledLabel: `${pct}%`,
        targetLabel: '100%',
      }
    })
  }, [data, productionView])

  const productionYAxis = useMemo(() => {
    if (productionView === 'Percentage') {
      return {
        domain: [0, 100] as [number, number],
        ticks: [0, 25, 50, 75, 100],
        tickFormatter: (v: number) => `${v}%`,
      }
    }
    const values = productionChartData.flatMap((row) => [
      row.scheduledValue,
      row.targetValue,
    ])
    return computeDynamicDollarAxis(values)
  }, [productionView, productionChartData])

  const noShowsTotal = data?.noShows.value ?? 0

  const noShowsChartData = useMemo(() => {
    if (!data) return []
    const total = noShowsTotal || 1
    return data.noShowsByPeriod.buckets.map((row) => {
      if (noShowsView === 'Count') {
        return {
          month: row.label,
          barValue: row.value,
          topLabel: String(row.value),
        }
      }
      const pct = Math.round((row.value / total) * 1000) / 10
      return {
        month: row.label,
        barValue: pct,
        topLabel: `${pct}%`,
      }
    })
  }, [data, noShowsView, noShowsTotal])

  const noShowsYAxis = useMemo(() => {
    if (noShowsView === 'Percentage') {
      return computePercentAxis(noShowsChartData.map((row) => row.barValue))
    }
    return computeDynamicCountAxis(noShowsChartData.map((row) => row.barValue))
  }, [noShowsView, noShowsChartData])

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.patientsSeen.value,
      data?.avgFilledSlots.value,
      data?.avgOpenSlots.value,
      data?.avgHygieneGaps.value,
      data?.noShows.value,
    ),
  )
  const filledOpenEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.filledSlotsByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ) ||
      (data?.openSlotsByPeriod?.buckets ?? []).some((row) =>
        hasNonZeroDashboardValues(row.value),
      ),
  )
  const hygieneEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.hygieneGapsByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const productionEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.scheduledProductionByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const noShowsEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.noShowsByPeriod?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel reports-scheduled-appointments">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Scheduled Appointments">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={SCHEDULED_APPOINTMENTS_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      {kpiEmpty ? (
        <DashboardSectionEmptyState />
      ) : (
        <ReportKpiGrid items={kpiItems} />
      )}

      <div className="reports-treatment-conversion-funnel__stack">
        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Average Filled vs Open Slots
                </ReportSectionHeading>
                {filledOpenEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={filledOpenView}
                    onChange={(value) =>
                      setFilledOpenView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {filledOpenEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={filledOpenChartData}
                      margin={REPORT_BAR_CHART_MARGIN_LEGEND}
                      barSize={50}
                      barGap={REPORT_GROUPED_BAR_GAP}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={filledOpenYAxis.domain}
                        ticks={filledOpenYAxis.ticks}
                        tickFormatter={filledOpenYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          filledOpenView === 'Count' ? value : `${value}%`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={REPORT_LEGEND_WRAPPER_STYLE}
                      />
                      <Bar
                        dataKey="filledValue"
                        name="Average Filled"
                        fill="var(--blue-400)"
                        radius={[6, 6, 6, 6]}
                      />
                      <Bar
                        dataKey="openValue"
                        name="Average Open"
                        fill="var(--orange-300)"
                        radius={[6, 6, 6, 6]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>Hygiene Gaps</ReportSectionHeading>
                {hygieneEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={hygieneView}
                    onChange={(value) =>
                      setHygieneView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {hygieneEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={hygieneChartData}
                      margin={REPORT_BAR_CHART_MARGIN}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={hygieneYAxis.domain}
                        ticks={hygieneYAxis.ticks}
                        tickFormatter={hygieneYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          hygieneView === 'Count' ? value : `${value}%`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="barValue"
                        fill="var(--blue-400)"
                        radius={[8, 8, 8, 8]}
                        barSize={100}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>

        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Production Target vs Scheduled
                </ReportSectionHeading>
                {productionEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={productionView}
                    onChange={(value) =>
                      setProductionView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {productionEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={productionChartData}
                      margin={REPORT_BAR_CHART_MARGIN_LEGEND}
                      barSize={50}
                      barGap={REPORT_GROUPED_BAR_GAP}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={productionYAxis.domain}
                        ticks={productionYAxis.ticks}
                        tickFormatter={productionYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          productionView === 'Count'
                            ? formatProductionDollars(value)
                            : `${value}%`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={REPORT_LEGEND_WRAPPER_STYLE}
                      />
                      <Bar
                        dataKey="scheduledValue"
                        name="Scheduled"
                        fill="var(--blue-400)"
                        radius={[6, 6, 6, 6]}
                      />
                      <Bar
                        dataKey="targetValue"
                        name="Target"
                        fill="var(--orange-300)"
                        radius={[6, 6, 6, 6]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>No-shows</ReportSectionHeading>
                {noShowsEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={noShowsView}
                    onChange={(value) =>
                      setNoShowsView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {noShowsEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={noShowsChartData}
                      margin={REPORT_BAR_CHART_MARGIN}
                      barCategoryGap={REPORT_BAR_CATEGORY_GAP}
                    >
                      <CartesianGrid
                        vertical={false}
                        strokeDasharray="0"
                        stroke={REPORT_CHART_GRID_STROKE}
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                      />
                      <YAxis
                        domain={noShowsYAxis.domain}
                        ticks={noShowsYAxis.ticks}
                        tickFormatter={noShowsYAxis.tickFormatter}
                        axisLine={false}
                        tickLine={false}
                        tick={REPORT_CHART_AXIS_TICK}
                        width={REPORT_YAXIS_WIDTH}
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          noShowsView === 'Count' ? value : `${value}%`
                        }
                        contentStyle={REPORT_CHART_TOOLTIP_STYLE}
                      />
                      <Bar
                        dataKey="barValue"
                        fill="var(--blue-400)"
                        radius={[8, 8, 8, 8]}
                        barSize={100}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>
      </div>
    </div>
  )
}

export default ScheduledAppointmentsReport
